/**
 * CANONICAL MODULE — app-platform/shared/themeColour.ts
 *
 * Copied byte-for-byte into every app that takes the theme engine (`src/lib/themeColour.ts`). Pure:
 * no DOM, no app import. **Fix bugs HERE, then sync outward; never edit an app's copy in place.**
 *
 * ── What it is ───────────────────────────────────────────────────────────────────────────────────
 * The colour primitives the theme engine (task 3.3, design/W-theme-contract.md §6) is built on, and
 * the "one luminance function" the small-utils audit asked for: parse a CSS colour literal, WCAG
 * relative luminance and contrast ratio, alpha compositing, HSL both ways, OKLab/OKLCH for mixing.
 *
 * ── Exactness, because two apps' outputs are pinned to these functions ─────────────────────────
 * - Luminance uses the WCAG 2.x sRGB transfer with the 0.04045 knee. Quil's copies use the older
 *   0.03928; for 8-bit channels the two can never disagree, because no c/255 lies in
 *   [0.03928, 0.04045) (10/255 = 0.039216, 11/255 = 0.043137). Asserted in the tests.
 * - `hslToRgb` is quil's `ground.ts` formula verbatim (h in degrees, s and l in 0-100, each channel
 *   `Math.round`ed but NOT clamped: an out-of-range l gives an out-of-range channel, exactly as quil
 *   computes contrast on it today). `toHex` clamps, as quil's does.
 * - `hslToHexUnit` is contently's `color.ts` `hslToHex` verbatim (s and l in 0-1). The accent
 *   solver uses it, so contently's six accents come out bit-identical. The two HSL formulas do NOT
 *   always agree: they round differently by one step on some inputs (hsl(130 50% 35%) is #2d863b by
 *   quil's, #2d863c by contently's; see the tests). Merging them would move one app's outputs, so
 *   each solver keeps the formula its app shipped with.
 *
 * ── Runtime module (2026-11-01) ──────────────────────────────────────────────────────────────────
 * Apps import this at runtime (quil's ground solver and inkling's accent solver put it in the entry
 * chunk), so it holds only what a running app calls. The `color-mix()` evaluator the token contract
 * needs lives in themeColourMix.ts, which no runtime module imports (shared/README.md, "Runtime versus
 * contract/tool-only theme modules").
 */

/** An sRGB colour, channels 0-255 (not necessarily integers once composited). */
export type RGB = [number, number, number]
/** An sRGB colour with alpha 0-1. */
export type RGBA = [number, number, number, number]

/** One adjustment a solver made (or one input it judged), for the builder to show. Shared by the
 *  ground and accent solvers and the contract's composer. */
export interface SolveStep {
  role: string
  from: string
  to: string
  why: string
}

const clamp = (v: number, lo: number, hi: number): number => Math.min(hi, Math.max(lo, v))

/**
 * Parse a CSS colour literal: `#rgb`, `#rgba`, `#rrggbb`, `#rrggbbaa`, `rgb()`/`rgba()` (comma or
 * space syntax, `/ alpha`, percentages), `hsl()`/`hsla()`, and the keywords `black`, `white`,
 * `transparent`. Anything else (a `var()`, a `color-mix()`, `oklch(from ...)`, junk) is `null`: the
 * caller decides whether that is an error. Never guesses a grey.
 *
 * A `color-mix()` is null HERE by design (2026-11-01): only the token-contract suite reads one, and
 * it uses `parseColourOrMix` from themeColourMix.ts, which is this plus the evaluator. No app reads a
 * mix at runtime (theme codes are six-digit hex, profiles hold literals), so the evaluator stays out of
 * every production bundle.
 */
export function parseColour(input: string): RGBA | null {
  const s = input.trim().toLowerCase()
  if (s === 'black') return [0, 0, 0, 1]
  if (s === 'white') return [255, 255, 255, 1]
  if (s === 'transparent') return [0, 0, 0, 0]
  if (s.startsWith('#')) {
    let h = s.slice(1)
    if (!/^[0-9a-f]+$/.test(h) || ![3, 4, 6, 8].includes(h.length)) return null
    if (h.length <= 4) h = h.split('').map((c) => c + c).join('')
    const n = (i: number): number => parseInt(h.slice(i, i + 2), 16)
    return [n(0), n(2), n(4), h.length === 8 ? n(6) / 255 : 1]
  }
  const fn = /^(rgba?|hsla?)\(\s*([^)]*)\)$/.exec(s)
  if (!fn) return null
  const kind = fn[1] ?? ''
  const body = (fn[2] ?? '').replace(/\s*\/\s*/, ' / ')
  const parts = body.includes(',') ? body.split(/\s*,\s*/) : body.split(/\s+/).filter((p) => p !== '/')
  if (parts.length < 3 || parts.length > 4) return null
  const num = (p: string | undefined, pctScale: number): number | null => {
    if (p === undefined) return null
    const m = /^(-?\d*\.?\d+)(%|deg)?$/.exec(p)
    if (!m) return null
    const v = Number(m[1])
    return m[2] === '%' ? (v / 100) * pctScale : v
  }
  const a = parts.length === 4 ? num(parts[3], 1) : 1
  if (a === null) return null
  if (kind.startsWith('rgb')) {
    const r = num(parts[0], 255)
    const g = num(parts[1], 255)
    const b = num(parts[2], 255)
    if (r === null || g === null || b === null) return null
    return [clamp(r, 0, 255), clamp(g, 0, 255), clamp(b, 0, 255), clamp(a, 0, 1)]
  }
  const h = num(parts[0], 360)
  const sat = num(parts[1], 100)
  const lig = num(parts[2], 100)
  if (h === null || sat === null || lig === null) return null
  const [r, g, b] = hslToRgb(((h % 360) + 360) % 360, clamp(sat, 0, 100), clamp(lig, 0, 100))
  return [r, g, b, clamp(a, 0, 1)]
}

/** `parseColour` that throws, naming the input, for callers that hold a contract of literals. */
export function mustParse(input: string): RGBA {
  const c = parseColour(input)
  if (!c) throw new Error(`not a colour literal: ${input}`)
  return c
}

/** Composite a colour with alpha over an opaque background (straight alpha, sRGB, unrounded). */
export function composite(fg: RGBA, bg: RGB): RGB {
  const a = fg[3]
  return [fg[0] * a + bg[0] * (1 - a), fg[1] * a + bg[1] * (1 - a), fg[2] * a + bg[2] * (1 - a)]
}

function lin(c: number): number {
  const x = c / 255
  return x <= 0.04045 ? x / 12.92 : Math.pow((x + 0.055) / 1.055, 2.4)
}

/** WCAG 2.x relative luminance of an sRGB colour (0 black, 1 white). */
export function luminance([r, g, b]: RGB | RGBA): number {
  return 0.2126 * lin(r) + 0.7152 * lin(g) + 0.0722 * lin(b)
}

/** WCAG contrast ratio of two opaque colours (1-21). Alpha, if present, is ignored: composite first. */
export function contrast(a: RGB | RGBA, b: RGB | RGBA): number {
  const la = luminance(a)
  const lb = luminance(b)
  return (Math.max(la, lb) + 0.05) / (Math.min(la, lb) + 0.05)
}

/** Contrast of two colour strings; a translucent `fg` is composited over `bg` first. Throws on a
 *  non-literal, naming it. */
export function contrastOf(fg: string, bg: string): number {
  const b = mustParse(bg)
  const f = mustParse(fg)
  const ground: RGB = [b[0], b[1], b[2]]
  return contrast(f[3] < 1 ? composite(f, ground) : f, ground)
}

/** `#rrggbb`, each channel rounded and clamped. */
export function toHex([r, g, b]: RGB | RGBA): string {
  return '#' + [r, g, b].map((c) => Math.max(0, Math.min(255, Math.round(c))).toString(16).padStart(2, '0')).join('')
}

/** HSL (h degrees, s and l 0-100) to RGB. Quil's formula verbatim: rounded, NOT clamped. */
export function hslToRgb(h: number, s: number, l: number): RGB {
  const sn = s / 100
  const ln = l / 100
  const k = (n: number): number => (n + h / 30) % 12
  const a = sn * Math.min(ln, 1 - ln)
  const f = (n: number): number => ln - a * Math.max(-1, Math.min(k(n) - 3, Math.min(9 - k(n), 1)))
  return [Math.round(f(0) * 255), Math.round(f(8) * 255), Math.round(f(4) * 255)]
}

/** RGB to HSL: h degrees, s and l 0-100. A grey has h 0, s 0. */
export function rgbToHsl([r, g, b]: RGB | RGBA): { h: number; s: number; l: number } {
  const [h, s, l] = rgbToHslUnit(r, g, b)
  return { h, s: s * 100, l: l * 100 }
}

/** RGB to HSL with s and l in 0-1 (contently's `rgbToHsl` verbatim). */
export function rgbToHslUnit(r: number, g: number, b: number): [number, number, number] {
  const rn = r / 255
  const gn = g / 255
  const bn = b / 255
  const mx = Math.max(rn, gn, bn)
  const mn = Math.min(rn, gn, bn)
  const l = (mx + mn) / 2
  let h = 0
  let s = 0
  const d = mx - mn
  if (d !== 0) {
    s = l > 0.5 ? d / (2 - mx - mn) : d / (mx + mn)
    if (mx === rn) h = (gn - bn) / d + (gn < bn ? 6 : 0)
    else if (mx === gn) h = (bn - rn) / d + 2
    else h = (rn - gn) / d + 4
    h *= 60
  }
  return [h, s, l]
}

function hue2rgb(p: number, q: number, t: number): number {
  if (t < 0) t += 1
  if (t > 1) t -= 1
  if (t < 1 / 6) return p + (q - p) * 6 * t
  if (t < 1 / 2) return q
  if (t < 2 / 3) return p + (q - p) * (2 / 3 - t) * 6
  return p
}

/** HSL (s and l 0-1) to `#rrggbb`: contently's `hslToHex` verbatim. Expects l in 0-1. */
export function hslToHexUnit(h: number, s: number, l: number): string {
  const hh = (((h % 360) + 360) % 360) / 360
  let r: number
  let g: number
  let b: number
  if (s === 0) {
    r = g = b = l
  } else {
    const q = l < 0.5 ? l * (1 + s) : l + s - l * s
    const p = 2 * l - q
    r = hue2rgb(p, q, hh + 1 / 3)
    g = hue2rgb(p, q, hh)
    b = hue2rgb(p, q, hh - 1 / 3)
  }
  const to = (x: number): string => Math.round(x * 255).toString(16).padStart(2, '0')
  return `#${to(r)}${to(g)}${to(b)}`
}

// ── OKLab / OKLCH (Björn Ottosson's matrices), for perceptual mixing of the optional roles ────────

/** sRGB (0-255) to OKLab [L 0-1, a, b]. */
export function rgbToOklab([r, g, b]: RGB | RGBA): [number, number, number] {
  const lr = lin(r)
  const lg = lin(g)
  const lb = lin(b)
  const l = Math.cbrt(0.4122214708 * lr + 0.5363325363 * lg + 0.0514459929 * lb)
  const m = Math.cbrt(0.2119034982 * lr + 0.6806995451 * lg + 0.1073969566 * lb)
  const s = Math.cbrt(0.0883024619 * lr + 0.2817188376 * lg + 0.6299787005 * lb)
  return [
    0.2104542553 * l + 0.793617785 * m - 0.0040720468 * s,
    1.9779984951 * l - 2.428592205 * m + 0.4505937099 * s,
    0.0259040371 * l + 0.7827717662 * m - 0.808675766 * s,
  ]
}

function delin(x: number): number {
  const v = x <= 0.0031308 ? 12.92 * x : 1.055 * Math.pow(x, 1 / 2.4) - 0.055
  return clamp(v, 0, 1) * 255
}

/**
 * OKLab to sRGB (0-255), clamped into gamut channel by channel.
 * Exported for tests only (app-platform 8.3): no app code calls it.
 */
export function oklabToRgb([L, a, b]: [number, number, number]): RGB {
  const l = (L + 0.3963377774 * a + 0.2158037573 * b) ** 3
  const m = (L - 0.1055613458 * a - 0.0638541728 * b) ** 3
  const s = (L - 0.0894841775 * a - 1.291485548 * b) ** 3
  return [
    delin(4.0767416621 * l - 3.3077115913 * m + 0.2309699292 * s),
    delin(-1.2684380046 * l + 2.6097574011 * m - 0.3413193965 * s),
    delin(-0.0041960863 * l - 0.7034186147 * m + 1.707614701 * s),
  ]
}

/** Mix two colours in OKLab: `t` 0 gives `a`, 1 gives `b`. Returns `#rrggbb`. */
export function mixOklab(a: string, b: string, t: number): string {
  const pa = rgbToOklab(mustParse(a))
  const pb = rgbToOklab(mustParse(b))
  const k = clamp(t, 0, 1)
  return toHex(oklabToRgb([pa[0] + (pb[0] - pa[0]) * k, pa[1] + (pb[1] - pa[1]) * k, pa[2] + (pb[2] - pa[2]) * k]))
}

/** Shift a colour's OKLCH lightness by `dL` (-1..1), hue and chroma kept. Returns `#rrggbb`. */
export function shiftLightness(c: string, dL: number): string {
  const [L, a, b] = rgbToOklab(mustParse(c))
  return toHex(oklabToRgb([clamp(L + dL, 0, 1), a, b]))
}
